import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Filter, Clock, CheckCircle, AlertCircle, Ban, Mail, MailOpen } from 'lucide-react';
import { vendorInquiryApi } from '../services/api';
import type { FairInquiryResponse, InquiryStatus } from '../types';
import { useNavigate } from 'react-router-dom';
import { extractApiError } from '../utils/errorUtils';

const VendorInquiryInbox: React.FC = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<FairInquiryResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Fetch inquiries
  const fetchInquiries = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await vendorInquiryApi.getMyInquiries(
        undefined,  // fairId
        statusFilter === 'all' ? undefined : statusFilter,
        page,
        20  // page size
      );

      setInquiries(response.items);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to fetch inquiries:', apiError);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, page]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  // Get status badge styling
  const getStatusBadge = (status: InquiryStatus) => {
    const badges = {
      new: {
        icon: Mail,
        className: 'bg-moonbone text-ink',
        label: 'New',
      },
      in_progress: {
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-800',
        label: 'In Progress',
      },
      waiting_for_vendor: {
        icon: AlertCircle,
        className: 'bg-orange-100 text-orange-800',
        label: 'Awaiting Your Response',
      },
      resolved: {
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800',
        label: 'Resolved',
      },
      closed: {
        icon: Ban,
        className: 'bg-gray-100 text-gray-800',
        label: 'Closed',
      },
    };

    const badge = badges[status];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  // Get priority badge styling
  const getPriorityBadge = (priority: string) => {
    const priorities = {
      urgent: { className: 'bg-red-100 text-red-800 border-red-200', label: 'Urgent' },
      high: { className: 'bg-orange-100 text-orange-800 border-orange-200', label: 'High' },
      normal: { className: 'bg-gray-100 text-gray-600 border-gray-200', label: 'Normal' },
      low: { className: 'bg-gray-50 text-gray-500 border-gray-100', label: 'Low' },
    };

    const badge = priorities[priority as keyof typeof priorities] || priorities.normal;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Loading state
  if (isLoading && inquiries.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2 text-gray-600">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span>Loading inquiries...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <MessageSquare className="h-8 w-8 text-secondary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Inquiries</h1>
              <p className="text-sm text-gray-600 mt-1">
                {total} {total === 1 ? 'inquiry' : 'inquiries'} total
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as InquiryStatus | 'all');
                  setPage(1);  // Reset to first page
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
              >
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_for_vendor">Awaiting My Response</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-900">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={fetchInquiries}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Inquiries List */}
        {inquiries.length === 0 && !isLoading ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No inquiries found</h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all'
                ? "You haven't created any inquiries yet."
                : `No inquiries with status "${statusFilter}".`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                onClick={() => navigate(`/vendor/inquiries/${inquiry.id}`)}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer p-6 border-l-4 border-primary"
              >
                <div className="flex items-start justify-between">
                  {/* Left: Inquiry details */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {inquiry.unread_count > 0 ? (
                        <Mail className="h-5 w-5 text-secondary" />
                      ) : (
                        <MailOpen className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-medium text-gray-900">{inquiry.subject}</h3>
                    </div>

                    <div className="flex items-center space-x-4 mb-3">
                      <span className="text-sm text-gray-600">
                        <strong>Fair:</strong> {inquiry.fair_name}
                      </span>
                      <span className="text-sm text-gray-600">
                        <strong>Type:</strong> {inquiry.inquiry_type.replace('_', ' ')}
                      </span>
                    </div>

                    <div className="flex items-center space-x-3">
                      {getStatusBadge(inquiry.status)}
                      {getPriorityBadge(inquiry.priority)}
                      {inquiry.unread_count > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-moonbone text-ink">
                          {inquiry.unread_count} unread
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: Timestamp */}
                  <div className="text-sm text-gray-500 text-right">
                    <div>{formatDate(inquiry.last_message_at || inquiry.created_at)}</div>
                    {inquiry.last_message_at && (
                      <div className="text-xs mt-1">Last message</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <span className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>

            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorInquiryInbox;
